import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Tag } from '@ontruck/orc';

import { StyledRow, StyledCell } from './styled';

import { compose, withHandlers } from 'recompose';
import { getShipperOrder } from 'redux/modules/shipper-orders/selectors';

import {
  StyledGrowCell,
  StyledScrollableCell,
} from 'modules/shipper-order/Table/ShipperOrderTableRow/styled';

import {
  IdCell,
  LoadSummaryCell,
  WeightCell,
  HeightCell,
  ExtrasSummary,
  IncidentsCell,
  AlertsCell,
} from 'modules/Common/Details';

import GoogleMapsLink from 'modules/Common/GoogleMapsLink';

import ShipperCell from 'modules/shipper-order/Table/ShipperOrderTableRow/ShipperCell';
import StopCell from 'modules/shipper-order/Table/ShipperOrderTableRow/StopCell';

import { RoutingChannel } from 'modules/tab-broadcasting';

import { isLockedToAgents } from 'modules/locked-to-agents';

const enhance = compose(
  connect((state, ownProps) => ({
    item: getShipperOrder(state, ownProps.itemId),
  })),
  withHandlers({
    handleClick:
      ({ onClick, itemId }) =>
      (e) => {
        if (!itemId || !onClick) {
          return;
        }

        onClick(e, itemId);
      },
  })
);

const t = {
  insured: 'Insured',
  new: 'New',
  header_id: 'Order Id',
  header_shipper: 'Shipper',
  header_pallets: 'Pallets',
  header_weight: 'Weight',
  header_height: 'Height',
  header_extras: 'Extras',
  header_incidents: 'Incid.',
  header_distance: 'Distance',
  header_stops: 'Stops',
  not_reviewed: 'Not reviewed',
};

const TableRow = React.memo(({ item, style, onClick, handleClick }) => {
  const { id } = item || {};

  const handleIdClick = useCallback(() => {
    if (!RoutingChannel || isLockedToAgents(item)) {
      return;
    }

    RoutingChannel.openShipperOrder(id);
  }, [id, item]);

  if (!item) {
    return null;
  }

  return (
    <StyledRow
      style={style}
      onClick={handleClick}
      clickable={onClick}
      data-e2e={`shipperOrder_row__${item.id}`}
      actionsDiabled={isLockedToAgents(item)}
      data-testid="ShipperOrderTableRow"
    >
      <StyledCell cellWidth={84} mLeft="S">
        <Fragment>
          <IdCell
            id={item.id}
            idType="shipperorder"
            isRecurring={!!item.recurring_order}
            copiable
            onClick={handleIdClick}
          />
          {item.insured_for > 0 && (
            <Tag skin="dark" size="S" weight="bold" textTransform="uppercase">
              {t.insured}
            </Tag>
          )}
        </Fragment>
      </StyledCell>

      <StyledCell cellWidth={376}>
        <ShipperCell shipper={item.shipper} isCore={item.is_core} />
      </StyledCell>

      <StyledCell cellWidth={60} textAlign="right">
        <LoadSummaryCell {...item} showEquivalents />
      </StyledCell>

      <StyledCell cellWidth={72} textAlign="right">
        <WeightCell {...item} />
      </StyledCell>

      <StyledCell cellWidth={48} textAlign="right">
        <HeightCell {...item} showIcon={false} />
      </StyledCell>

      <StyledCell cellWidth={128}>
        <ExtrasSummary
          {...item}
          config={{
            additionalDocuments: {
              color: 'error700',
            },
          }}
          small
        />
      </StyledCell>

      <StyledCell cellWidth={24}>
        <AlertsCell {...item} />
      </StyledCell>

      <StyledCell cellWidth={24} textAlign="center">
        <Fragment>
          {!!item.number_of_incidents && (
            <IncidentsCell
              numberOfIncidents={item.number_of_incidents}
              fullText={false}
              shipperOrderId={item.id}
            />
          )}
        </Fragment>
      </StyledCell>

      <StyledCell cellWidth={56}>
        <GoogleMapsLink showIcon={false} skin="primary" item={item} idType="shipperorder" />
      </StyledCell>

      <StyledGrowCell>
        <StyledScrollableCell>
          {(item.stops || []).map((stop, key) => (
            <StopCell
              key={key}
              cellWidth={184}
              firstStopTimeFrom={((item.stops || [])[0] || {}).time_from}
              stop={stop}
            />
          ))}
        </StyledScrollableCell>
      </StyledGrowCell>
    </StyledRow>
  );
});

TableRow.propTypes = {
  item: PropTypes.object,
  style: PropTypes.object,
  handleClick: PropTypes.func,
  onClick: PropTypes.func,
  ref: PropTypes.any,
};

export default enhance(TableRow);
